import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  PlayIcon, 
  UserGroupIcon, 
  ClockIcon,
  ArrowLeftIcon,
  CalendarIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { apiService, Show, Episode, Panelist } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAudio } from '../contexts/AudioContext';

const Episodes: React.FC = () => {
  const { showId } = useParams<{ showId: string }>();
  const navigate = useNavigate();
  const { startSession } = useAudio();
  const [show, setShow] = useState<Show | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [panelists, setPanelists] = useState<Panelist[]>([]);
  const [loading, setLoading] = useState(true);
  const [startingSession, setStartingSession] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!showId) return;
      
      try {
        const [showData, episodesData, panelistsData] = await Promise.all([
          apiService.getShow(showId),
          apiService.getEpisodes(50, 0, showId),
          apiService.getPanelists()
        ]);
        setShow(showData);
        setEpisodes(episodesData);
        setPanelists(panelistsData);
      } catch (error) {
        console.error('Error fetching episodes data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [showId]);

  const handleStartSession = async (episodeId: string, episodeTitle: string) => {
    try {
      setStartingSession(episodeId);
      await startSession(episodeId, episodeTitle);
      // Audio will start playing automatically via the floating player
    } catch (error) {
      console.error('Error starting session:', error);
      alert('Failed to start session. Please try again.');
    } finally {
      setStartingSession(null);
    }
  };

  const getPanelistNames = (panelistIds: string[]) => {
    return panelists
      .filter(p => panelistIds.includes(p._id))
      .map(p => p.name)
      .join(', ');
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'Unknown';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!show) {
    return (
      <div className="text-center py-12">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Show Not Found
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          The show you're looking for doesn't exist or is not available.
        </p>
        <button
          onClick={() => navigate('/channels')}
          className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          <span>Back to Channels</span>
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => navigate('/channels')}
          className="p-2 text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
        >
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {show.name}
          </h1>
          {show.description && (
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {show.description}
            </p>
          )}
        </div>
      </div>

      {/* Show Details */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="flex items-center space-x-2">
            <UserGroupIcon className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Panelists</p>
              <p className="font-semibold text-gray-900 dark:text-white">{show.panelistIds.length}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <ClockIcon className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Frequency</p>
              <p className="font-semibold text-gray-900 dark:text-white capitalize">{show.frequency}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <CalendarIcon className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Episodes</p>
              <p className="font-semibold text-gray-900 dark:text-white">{episodes.length}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <TagIcon className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Category</p>
              <p className="font-semibold text-gray-900 dark:text-white">{show.category || 'General'}</p>
            </div>
          </div>
        </div>

        {/* Panelist Names */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Featuring:</p>
          <p className="text-gray-900 dark:text-white font-medium">
            {getPanelistNames(show.panelistIds)}
          </p>
        </div>

        {/* Show Tags */}
        {show.tags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {show.tags.map((tag, index) => (
              <span key={index} className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full text-xs">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Episodes */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          Episodes
        </h2>
        
        {episodes.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {episodes.map((episode) => (
              <div
                key={episode._id}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 group"
              >
                {/* Episode Header */}
                <div className="mb-4">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors line-clamp-2">
                      {episode.title}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      episode.status === 'published' 
                        ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}>
                      {episode.status}
                    </span>
                  </div>
                  
                  {episode.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 mb-4">
                      {episode.description}
                    </p>
                  )}
                </div>

                {/* Episode Details */}
                <div className="space-y-3 mb-6">
                  {episode.episodeNumber && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Episode {episode.episodeNumber}</span>
                      {episode.seasonNumber && <span>• Season {episode.seasonNumber}</span>}
                    </div>
                  )}
                  
                  {episode.duration && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                      <ClockIcon className="w-4 h-4" />
                      <span>{formatDuration(episode.duration)}</span>
                    </div>
                  )}

                  {episode.wordCount && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                      <span>{episode.wordCount.toLocaleString()} words</span>
                    </div>
                  )}

                  {episode.segmentCount && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                      <span>{episode.segmentCount} segments</span>
                    </div>
                  )}

                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <CalendarIcon className="w-4 h-4" />
                    <span>
                      {new Date(episode.publishedAt || episode.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Episode Tags */}
                {episode.tags.length > 0 && (
                  <div className="mb-6">
                    <div className="flex flex-wrap gap-1">
                      {episode.tags.slice(0, 3).map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full text-xs">
                          {tag}
                        </span>
                      ))}
                      {episode.tags.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-xs">
                          +{episode.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Button */}
                <button
                  onClick={() => handleStartSession(episode._id, episode.title)}
                  disabled={startingSession === episode._id || episode.status !== 'published'}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center space-x-2 transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {startingSession === episode._id ? (
                    <>
                      <div className="w-5 h-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      <span>Starting...</span>
                    </>
                  ) : (
                    <>
                      <PlayIcon className="w-5 h-5" />
                      <span>Start Listening</span>
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900 dark:to-blue-900 rounded-full flex items-center justify-center mx-auto mb-6">
              <PlayIcon className="w-12 h-12 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              No Episodes Available
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
              This show doesn't have any published episodes yet. Check back soon for new content!
            </p>
            <button
              onClick={() => navigate('/channels')}
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Back to Channels</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Episodes;
